<template>
  <transition name="modal-fade">
    <div v-if="isVisible" class="modal-overlay" @click.self="closeModal">
      <div class="settings-modal modal-container">
        <div class="modal-header">
          <h2><i class="fas fa-cog"></i> CONFIGURAÇÕES</h2>
          <button class="close-button" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <div class="tabs">
          <button 
            v-for="tab in tabs" 
            :key="tab.id" 
            @click="activeTab = tab.id"
            :class="{ active: activeTab === tab.id }"
            class="tab-button"
          >
            <i :class="tab.icon"></i> {{ tab.name }}
          </button>
        </div>
        
        <div class="tab-content">
          <!-- Language Tab -->
          <div v-if="activeTab === 'language'" class="language-settings">
            <h3>CONFIGURAÇÕES DE IDIOMA</h3>
            <div class="language-options">
              <button 
                v-for="lang in availableLanguages" 
                :key="lang" 
                @click="setLanguage(lang)"
                :class="{ active: currentLanguage === lang }"
                class="language-button"
              >
                {{ getLanguageName(lang) }}
              </button>
            </div>
          </div>
          
          <!-- Audio Tab -->
          <div v-if="activeTab === 'audio'" class="audio-settings">
            <h3>CONFIGURAÇÕES DE ÁUDIO</h3>
            
            <div class="setting-row">
              <label>Mutar Música</label>
              <button @click="toggleMusicMute" class="toggle-button" :class="{ active: isMusicMuted }">
                <span class="toggle-icon">
                  <i :class="isMusicMuted ? 'fas fa-volume-mute' : 'fas fa-music'"></i>
                </span>
                {{ isMusicMuted ? 'Ativar' : 'Mutar' }}
              </button>
            </div>
            
            <div class="setting-row">
              <label>Mutar Sons do Jogo</label>
              <button @click="toggleSoundMute" class="toggle-button" :class="{ active: isSoundMuted }">
                <span class="toggle-icon">
                  <i :class="isSoundMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
                </span>
                {{ isSoundMuted ? 'Ativar' : 'Mutar' }}
              </button>
            </div>
            
            <div class="setting-row">
              <label>Mutar Sons Ambiente</label>
              <button @click="toggleAmbientMute" class="toggle-button" :class="{ active: isAmbientMuted }">
                <span class="toggle-icon">
                  <i :class="isAmbientMuted ? 'fas fa-volume-mute' : 'fas fa-volume-up'"></i>
                </span>
                {{ isAmbientMuted ? 'Ativar' : 'Mutar' }}
              </button>
            </div>

            <div class="setting-row">
              <label>Mutar Sons de Click</label>
              <button @click="toggleUiClickMute" class="toggle-button" :class="{ active: isUiClickMuted }">
                <span class="toggle-icon">
                  <i :class="isUiClickMuted ? 'fas fa-volume-mute' : 'fas fa-mouse-pointer'"></i>
                </span>
                {{ isUiClickMuted ? 'Ativar' : 'Mutar' }}
              </button>
            </div>
            
            <div class="setting-row">
              <label>Volume da Música</label>
              <div class="volume-control">
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.01" 
                  v-model="musicVolume"
                  @input="updateMusicVolume"
                  :disabled="isMusicMuted"
                  class="cyber-slider"
                />
                <span class="volume-percentage">{{ Math.round(musicVolume * 100) }}%</span>
              </div>
            </div>
            
            <div class="setting-row">
              <label>Volume dos Efeitos Sonoros</label>
              <div class="volume-control">
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.01" 
                  v-model="soundVolume"
                  @input="updateSoundVolume"
                  :disabled="isSoundMuted"
                  class="cyber-slider"
                />
                <span class="volume-percentage">{{ Math.round(soundVolume * 100) }}%</span>
              </div>
            </div>
            
            <div class="setting-row">
              <label>Volume dos Sons Ambiente</label>
              <div class="volume-control">
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.01" 
                  v-model="ambientVolume"
                  @input="updateAmbientVolume"
                  :disabled="isAmbientMuted"
                  class="cyber-slider ambient-slider"
                />
                <span class="volume-percentage">{{ Math.round(ambientVolume * 100) }}%</span>
              </div>
            </div>
            
            <div class="music-player">
              <div class="music-info" v-if="currentMusicInfo">
                <h4>{{ currentMusicInfo.title }}</h4>
                <p>{{ currentMusicInfo.artist }}</p>
              </div>
              <div class="music-controls">
                <button @click="previousTrack" class="control-button">
                  <i class="fas fa-step-backward"></i>
                </button>
                <button @click="togglePlay" class="play-button">
                  <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                </button>
                <button @click="nextTrack" class="control-button">
                  <i class="fas fa-step-forward"></i>
                </button>
              </div>
            </div>
          </div>
          
          <!-- User Tab -->
          <div v-if="activeTab === 'user'" class="user-settings">
            <h3>CONFIGURAÇÕES DE USUÁRIO</h3>
            
            <div class="profile-access">
              <h4>SEU PERFIL</h4>
              <p>Acesse seu perfil para visualizar suas informações ou buscar outros jogadores.</p>
              <button @click="goToProfile" class="profile-button">
                <i class="fas fa-user-circle"></i>
                <span>VER PERFIL</span>
                <div class="button-glitch"></div>
              </button>
            </div>
            
            <!-- Nova seção de logout -->
            <div class="logout-section">
              <h4>SAIR DA CONTA</h4>
              <p>Deslogar da sua conta no Deep Ring.</p>
              <button @click="handleLogout" class="logout-button">
                <i class="fas fa-sign-out-alt"></i>
                <span>DESLOGAR</span>
                <div class="button-glitch"></div>
              </button>
            </div>
            
            <div class="password-change">
              <h4>ALTERAR SENHA</h4>
              <form @submit.prevent="changePassword">
                <div class="form-group">
                  <label>Senha Atual</label>
                  <input 
                    type="password" 
                    v-model="passwordForm.currentPassword" 
                    required
                  />
                </div>
                
                <div class="form-group">
                  <label>Nova Senha</label>
                  <input 
                    type="password" 
                    v-model="passwordForm.newPassword" 
                    required
                  />
                </div>
                
                <div class="form-group">
                  <label>Confirme a Nova Senha</label>
                  <input 
                    type="password" 
                    v-model="passwordForm.confirmPassword" 
                    required
                  />
                </div>
                
                <div v-if="passwordError" class="error-message">
                  <i class="fas fa-exclamation-triangle"></i> {{ passwordError }}
                </div>
                
                <div v-if="passwordSuccess" class="success-message">
                  <i class="fas fa-check-circle"></i> {{ passwordSuccess }}
                </div>
                
                <button type="submit" class="submit-button" :disabled="isSubmitting">
                  <span class="btn-glow"></span>
                  {{ isSubmitting ? 'PROCESSANDO...' : 'ALTERAR SENHA' }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Cookies from 'js-cookie'
import axios from 'axios'

export default {
  name: 'ModalConfigs',
  data() {
    return {
      activeTab: 'language',
      tabs: [
        { id: 'language', name: 'Idioma', icon: 'fas fa-language' },
        { id: 'audio', name: 'Áudio', icon: 'fas fa-volume-up' },
        { id: 'user', name: 'Usuário', icon: 'fas fa-user-cog' }
      ],
      musicVolume: 0.5,
      soundVolume: 0.7,
      ambientVolume: 0.5, // Add ambient volume control
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordError: '',
      passwordSuccess: '',
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters('Configs', ['isSettingsModalVisible', 'currentLanguage', 'availableLanguages']),
    isVisible() {
      return this.isSettingsModalVisible
    },
    isMusicMuted() {
      return this.$store.state.SoundController.isMusicMuted
    },
    isSoundMuted() {
      return this.$store.state.SoundController.isSoundMuted
    },
    isAmbientMuted() {
      return this.$store.state.SoundController.isAmbientMuted
    },
    currentMusicInfo() {
      return this.$store.state.SoundController.currentMusicInfo
    },
    isPlaying() {
      return this.$store.state.SoundController.isPlaying
    },
    isUiClickMuted() {
      return this.$store.state.SoundController.isUiClickMuted
    }
  },
  created() {
    // Initialize volume values from store
    this.musicVolume = this.$store.state.SoundController.musicVolume
    this.soundVolume = this.$store.state.SoundController.soundVolume
    this.ambientVolume = this.$store.state.SoundController.ambientVolume
  },
  methods: {
    ...mapActions('Configs', ['toggleSettingsModal', 'setLanguage']),
    ...mapActions('SoundController', [
      'updateVolumes', 
      'pauseMusic', 
      'resumeMusic',
      'switchMusic',
      'playSound'
    ]),
    closeModal() {
      if (!this.isUiClickMuted && !this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
      this.toggleSettingsModal(false)
    },
    getLanguageName(code) {
      const names = {
        'pt': 'Português',
        'en': 'English',
        'es': 'Español',
        'zh': '中文'
      }
      return names[code] || code
    },
    toggleMusicMute() {
      this.$store.commit('SoundController/TOGGLE_MUSIC_MUTE')
      this.updateVolumes()
      // Play UI sound effect
      if (!this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    toggleSoundMute() {
      this.$store.commit('SoundController/TOGGLE_SOUND_MUTE')
      
      // Update all audio elements with the new mute state
      this.updateAllSoundVolumes();
      
      // We need to toggle sound before playing the effect
      if (!this.$store.state.SoundController.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    updateMusicVolume() {
      this.$store.commit('SoundController/SET_MUSIC_VOLUME', parseFloat(this.musicVolume))
      this.updateVolumes()
    },
    updateSoundVolume() {
      this.$store.commit('SoundController/SET_SOUND_VOLUME', parseFloat(this.soundVolume));
      this.updateVolumes();
      
      // Importante: Aqui chamamos updateAllSoundVolumes que foi modificado 
      // para afetar APENAS efeitos sonoros regulares
      this.$store.dispatch('SoundController/updateAllSoundVolumes');
      
      // Log para depuração
      console.log(`Efeitos sonoros atualizados para ${this.soundVolume} - NÃO afeta sons ambiente`);
    },
    // New method to update all audio elements including city ambient
    updateAllSoundVolumes() {
      const soundVolume = this.soundVolume;
      const isMuted = this.$store.state.SoundController.isSoundMuted;
      
      // Find all audio elements in the document
      const activeAudioElements = document.querySelectorAll('audio');
      
      activeAudioElements.forEach(audio => {
        // Skip the background music which is controlled separately
        if (audio === this.$store.state.SoundController.currentMusic) return;
        
        // Check if this is a sound effect (not the music track)
        const isEffect = audio !== this.$store.state.SoundController.currentMusic;
        
        if (isEffect) {
          // Set volume to 0 if muted, otherwise to current sound volume
          audio.volume = isMuted ? 0 : soundVolume;
          
          // Log for debugging
          if (audio.dataset.soundType === 'cityAmbient') {
            console.log('Updated city ambient volume:', isMuted ? 0 : soundVolume);
          }
        }
      });
    },
    togglePlay() {
      if (this.isPlaying) {
        this.pauseMusic()
      } else {
        this.resumeMusic()
      }
      // Play UI sound effect
      if (!this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    previousTrack() {
      this.switchMusic('prev')
      if (!this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    nextTrack() {
      this.switchMusic('next')
      if (!this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
    },
    async changePassword() {
      // Reset messages
      this.passwordError = ''
      this.passwordSuccess = ''
      
      // Validate password match
      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = 'As senhas não coincidem.'
        return
      }
      
      // Get JWT token
      const token = Cookies.get('jwt')
      if (!token) {
        this.passwordError = 'Você precisa estar logado para alterar a senha.'
        return
      }
      
      this.isSubmitting = true
      
      try {
        // Call Strapi API to change password - removed unused variable
        await axios.post(
          `${process.env.VUE_APP_API_URL}/api/auth/change-password`,
          {
            currentPassword: this.passwordForm.currentPassword,
            password: this.passwordForm.newPassword,
            passwordConfirmation: this.passwordForm.confirmPassword
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
        
        this.passwordSuccess = 'Senha alterada com sucesso!'
        
        // Reset form
        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
        
        // Play success sound
        if (!this.isSoundMuted) {
          this.playSound({ category: 'ui', type: 'success' })
        }
      } catch (error) {
        console.error('Error changing password:', error)
        
        // Handle error messages from Strapi
        if (error.response && error.response.data) {
          this.passwordError = error.response.data.error.message || 'Erro ao alterar senha.'
        } else {
          this.passwordError = 'Erro ao comunicar com o servidor.'
        }
        
        // Play error sound
        if (!this.isSoundMuted) {
          this.playSound({ category: 'ui', type: 'error' })
        }
      } finally {
        this.isSubmitting = false
      }
    },
    goToProfile() {
      // Close the modal first
      this.closeModal();
      // Navigate to the profile page
      this.$router.push({ name: 'ProfilePlayer' });
    },
    // New methods for ambient sound controls
    toggleAmbientMute() {
      this.$store.dispatch('SoundController/toggleAmbientMute');
      
      // Play UI sound effect if sound effects are not muted
      if (!this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' });
      }
    },
    
    updateAmbientVolume() {
      // Convertemos explicitamente para float e usamos a action específica para sons ambiente
      this.$store.dispatch('SoundController/updateAmbientVolume', parseFloat(this.ambientVolume));
      
      // Update current battle's crowd sounds if a battle is in progress
      this.$store.dispatch('battle/updateCrowdVolume');
      
      console.log(`Volume ambiente atualizado para ${this.ambientVolume} - controla sons de torcida e ambiente`);
    },
    toggleUiClickMute() {
      this.$store.commit('SoundController/TOGGLE_UI_CLICK_MUTE')
      // Don't play UI click sound here since it would be confusing
    },
    // Novo método para lidar com o logout
    async handleLogout() {
      // Play click sound if not muted
      if (!this.isUiClickMuted && !this.isSoundMuted) {
        this.playSound({ category: 'ui', type: 'click' })
      }
      
      try {
        // Fechar o modal primeiro
        this.closeModal()
        
        // Chamar a ação de logout do store de autenticação
        await this.$store.dispatch('auth/logout')
        
        // Redirecionar para a página de login
        this.$router.push({ name: 'Login' })
      } catch (error) {
        console.error('Erro ao deslogar:', error)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700&family=Rajdhani:wght@400;500;700&display=swap');

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
}

.settings-modal {
  background: linear-gradient(135deg, #1e1e1e 0%, #0a0a0a 100%);
  border-radius: 3px;
  width: 95%; // Aumentado de 90% para 95% em mobile
  max-width: 550px;
  max-height: 90vh; // Aumentado de 85vh para 90vh
  margin: 1rem; // Adiciona margem para evitar colagem nas bordas
  overflow-y: auto;
  box-shadow: 0 0 30px rgba(196, 30, 58, 0.4), 0 0 10px rgba(0, 0, 0, 0.8);
  color: #e0e0e0;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(196, 30, 58, 0.5);
  font-family: 'Rajdhani', sans-serif;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #c41e3a, transparent);
  }
  
  &::-webkit-scrollbar {
    width: 8px;
    background: rgba(0, 0, 0, 0.3);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(196, 30, 58, 0.5);
    border-radius: 4px;
    
    &:hover {
      background: rgba(196, 30, 58, 0.8);
    }
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem;
    max-height: 95vh;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  border-bottom: 2px solid rgba(196, 30, 58, 0.3);
  background: rgba(0, 0, 0, 0.3);
  
  h2 {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    color: #c41e3a;
    text-shadow: 0 0 10px rgba(196, 30, 58, 0.5);
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    
    i {
      margin-right: 10px;
    }
  }
  
  .close-button {
    background: rgba(196, 30, 58, 0.2);
    border: 1px solid rgba(196, 30, 58, 0.3);
    color: #c41e3a;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 3px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(196, 30, 58, 0.4);
      color: #fff;
      box-shadow: 0 0 10px rgba(196, 30, 58, 0.5);
    }
  }
}

.tabs {
  display: flex;
  border-bottom: 1px solid rgba(196, 30, 58, 0.3);
  background: rgba(20, 20, 20, 0.7);
  
  .tab-button {
    flex: 1;
    background: transparent;
    border: none;
    color: #999;
    padding: 1rem 1rem;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 3px solid transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: 'Rajdhani', sans-serif;
    font-weight: 500;
    font-size: 1.1rem;
    letter-spacing: 0.5px;
    position: relative;
    
    i {
      font-size: 1rem;
    }
    
    &:hover {
      color: #ddd;
      background: rgba(196, 30, 58, 0.1);
    }
    
    &.active {
      color: #fff;
      border-bottom-color: #c41e3a;
      font-weight: 700;
      background: rgba(196, 30, 58, 0.15);
      
      &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 0;
        right: 0;
        height: 3px;
        background: #c41e3a;
        box-shadow: 0 0 8px #c41e3a, 0 0 15px rgba(196, 30, 58, 0.5);
      }
    }
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    
    .tab-button {
      padding: 0.8rem;
      border-bottom: 1px solid rgba(196, 30, 58, 0.3);
      
      &:last-child {
        border-bottom: none;
      }
    }
  }
}

.tab-content {
  padding: 1.5rem;
  
  h3 {
    margin-top: 0;
    margin-bottom: 1.5rem;
    font-size: 1.3rem;
    color: #c41e3a;
    font-weight: 700;
    text-align: center;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(196, 30, 58, 0.3);
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 35%;
      right: 35%;
      height: 2px;
      background: #c41e3a;
      box-shadow: 0 0 10px rgba(196, 30, 58, 0.8);
    }
  }
}

.language-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  .language-button {
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(196, 30, 58, 0.3);
    border-radius: 3px;
    padding: 1rem;
    color: #ddd;
    cursor: pointer;
    transition: all 0.3s;
    font-family: 'Rajdhani', sans-serif;
    font-weight: 500;
    font-size: 1.1rem;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.1), transparent);
      transition: 0.5s;
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.2);
      border-color: rgba(196, 30, 58, 0.5);
      transform: translateY(-2px);
      
      &::before {
        left: 100%;
      }
    }
    
    &.active {
      background: rgba(196, 30, 58, 0.3);
      border-color: #c41e3a;
      color: #fff;
      box-shadow: 0 0 15px rgba(196, 30, 58, 0.3);
      transform: translateY(-3px);
      
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: #c41e3a;
        box-shadow: 0 0 8px #c41e3a;
      }
    }
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr; // Muda para uma coluna em telas pequenas
    gap: 0.8rem;
    
    .language-button {
      padding: 0.8rem;
    }
  }
}

.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0.8rem;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(196, 30, 58, 0.1);
  
  &:hover {
    border-color: rgba(196, 30, 58, 0.3);
    background: rgba(196, 30, 58, 0.05);
  }
  
  label {
    font-size: 1rem;
    color: #ccc;
    font-weight: 500;
  }
  
  .toggle-button {
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(196, 30, 58, 0.3);
    border-radius: 3px;
    padding: 0.6rem 1rem;
    color: #ddd;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    
    .toggle-icon {
      display: inline-block;
      width: 22px;
      text-align: center;
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.2);
      border-color: rgba(196, 30, 58, 0.5);
    }
    
    &.active {
      background: rgba(196, 30, 58, 0.3);
      border-color: #c41e3a;
      color: #fff;
      
      i {
        color: #c41e3a;
      }
    }
  }
  
  .volume-control {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .cyber-slider {
      -webkit-appearance: none;
      width: 180px;
      height: 6px;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 3px;
      outline: none;
      border: 1px solid rgba(196, 30, 58, 0.3);
      
      &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 2px;
        background: #c41e3a;
        cursor: pointer;
        border: 1px solid #fff;
        box-shadow: 0 0 5px rgba(196, 30, 58, 0.8);
      }
      
      &::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 2px;
        background: #c41e3a;
        cursor: pointer;
        border: 1px solid #fff;
        box-shadow: 0 0 5px rgba(196, 30, 58, 0.8);
      }
      
      &:disabled {
        opacity: 0.5;
        border-color: rgba(255, 255, 255, 0.1);
      }
    }
    
    .volume-percentage {
      min-width: 50px;
      font-size: 1rem;
      color: #ccc;
      font-weight: 500;
      background: rgba(0, 0, 0, 0.3);
      padding: 0.2rem 0.5rem;
      border-radius: 3px;
      border: 1px solid rgba(196, 30, 58, 0.2);
      text-align: center;
    }
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.8rem;
    
    label {
      font-size: 0.9rem;
    }
    
    .volume-control {
      justify-content: space-between;
      
      .cyber-slider {
        width: calc(100% - 60px); // Ajusta largura considerando o percentual
      }
    }
  }
}

.music-player {
  margin-top: 2rem;
  border-top: 1px solid rgba(196, 30, 58, 0.3);
  padding-top: 1.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  padding: 1.5rem;
  border: 1px solid rgba(196, 30, 58, 0.2);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(196, 30, 58, 0.5), transparent);
  }
  
  .music-info {
    text-align: center;
    margin-bottom: 1.2rem;
    
    h4 {
      margin: 0 0 0.5rem 0;
      font-size: 1.2rem;
      color: #f0f0f0;
      font-family: 'Orbitron', sans-serif;
      letter-spacing: 1px;
    }
    
    p {
      margin: 0;
      font-size: 0.9rem;
      color: #999;
    }
  }
  
  .music-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    
    button {
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid rgba(196, 30, 58, 0.3);
      color: #ddd;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        background: rgba(196, 30, 58, 0.2);
        border-color: rgba(196, 30, 58, 0.5);
        color: #fff;
        box-shadow: 0 0 10px rgba(196, 30, 58, 0.3);
      }
    }
    
    .control-button {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      font-size: 0.9rem;
    }
    
    .play-button {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      font-size: 1.3rem;
      background: rgba(196, 30, 58, 0.3);
      border-color: rgba(196, 30, 58, 0.5);
      position: relative;
      
      &::after {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        border-radius: 50%;
        border: 2px solid transparent;
        background: linear-gradient(45deg, #c41e3a, transparent, #c41e3a) border-box;
        -webkit-mask: 
          linear-gradient(#fff 0 0) padding-box, 
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: destination-out;
        mask-composite: exclude;
      }
      
      &:hover {
        background: rgba(196, 30, 58, 0.5);
        box-shadow: 0 0 15px rgba(196, 30, 58, 0.5);
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    
    .music-info {
      h4 {
        font-size: 1rem;
      }
      
      p {
        font-size: 0.8rem;
      }
    }
    
    .music-controls {
      gap: 0.8rem;
      
      .control-button {
        width: 36px;
        height: 36px;
      }
      
      .play-button {
        width: 48px;
        height: 48px;
      }
    }
  }
}

.password-change {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  padding: 1.8rem;
  border: 1px solid rgba(196, 30, 58, 0.2);
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid #c41e3a;
  }
  
  &::before {
    top: -1px;
    left: -1px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -1px;
    right: -1px;
    border-left: none;
    border-top: none;
  }
  
  h4 {
    margin-top: 0;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    font-weight: 700;
    color: #c41e3a;
    text-align: center;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
    
    label {
      display: block;
      margin-bottom: 0.5rem;
      color: #ccc;
      font-size: 1rem;
      font-weight: 500;
    }
    
    input {
      width: 100%;
      padding: 0.8rem;
      border-radius: 3px;
      background: rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(196, 30, 58, 0.3);
      color: #e0e0e0;
      font-family: 'Rajdhani', sans-serif;
      
      &:focus {
        outline: none;
        border-color: #c41e3a;
        box-shadow: 0 0 5px rgba(196, 30, 58, 0.5);
      }
    }
  }
  
  .error-message {
    padding: 0.8rem;
    border-radius: 3px;
    margin-bottom: 1.2rem;
    background: rgba(196, 30, 58, 0.1);
    border: 1px solid rgba(196, 30, 58, 0.4);
    color: #ff6b6b;
    font-size: 0.95rem;
    position: relative;
    
    i {
      margin-right: 8px;
    }
  }
  
  .success-message {
    padding: 0.8rem;
    border-radius: 3px;
    margin-bottom: 1.2rem;
    background: rgba(0, 255, 0, 0.05);
    border: 1px solid rgba(0, 255, 0, 0.2);
    color: #5cff5c;
    font-size: 0.95rem;
    
    i {
      margin-right: 8px;
    }
  }
  
  .submit-button {
    width: 100%;
    padding: 1rem;
    background: rgba(196, 30, 58, 0.8);
    border: none;
    border-radius: 3px;
    color: white;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    
    .btn-glow {
      position: absolute;
      top: -10%;
      left: -10%;
      width: 120%;
      height: 50%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transform: skewX(-20deg);
      transition: 0.6s;
    }
    
    &:hover {
      background: #c41e3a;
      box-shadow: 0 0 15px rgba(196, 30, 58, 0.5);
      
      .btn-glow {
        transform: translateX(100%) skewX(-20deg);
      }
    }
    
    &:disabled {
      background: #444;
      cursor: not-allowed;
      box-shadow: none;
      
      .btn-glow {
        display: none;
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    
    .form-group {
      margin-bottom: 1rem;
      
      input {
        padding: 0.6rem;
      }
    }
  }
}

.profile-access {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  padding: 1.8rem;
  border: 1px solid rgba(196, 30, 58, 0.2);
  position: relative;
  margin-bottom: 2rem;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid #c41e3a;
  }
  
  &::before {
    top: -1px;
    left: -1px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -1px;
    right: -1px;
    border-left: none;
    border-top: none;
  }
  
  h4 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 700;
    color: #c41e3a;
    text-align: center;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
  }
  
  p {
    margin-bottom: 1.5rem;
    color: #ccc;
    text-align: center;
    font-size: 0.95rem;
  }
  
  .profile-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 1rem;
    background: rgba(196, 30, 58, 0.2);
    border: 1px solid rgba(196, 30, 58, 0.4);
    border-radius: 3px;
    color: white;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    
    i {
      font-size: 1.2rem;
    }
    
    .button-glitch {
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: 0.6s;
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(196, 30, 58, 0.3);
      
      .button-glitch {
        left: 100%;
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    
    p {
      font-size: 0.9rem;
    }
    
    .profile-button {
      padding: 0.8rem;
    }
  }
}

/* Estilo para a seção de logout */
.logout-section {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  padding: 1.8rem;
  border: 1px solid rgba(196, 30, 58, 0.2);
  position: relative;
  margin-bottom: 2rem;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid #c41e3a;
  }
  
  &::before {
    top: -1px;
    left: -1px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -1px;
    right: -1px;
    border-left: none;
    border-top: none;
  }
  
  h4 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 700;
    color: #c41e3a;
    text-align: center;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
  }
  
  p {
    margin-bottom: 1.5rem;
    color: #ccc;
    text-align: center;
    font-size: 0.95rem;
  }
  
  .logout-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 1rem;
    background: rgba(196, 30, 58, 0.5);
    border: 1px solid rgba(196, 30, 58, 0.6);
    border-radius: 3px;
    color: white;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s;
    font-family: 'Orbitron', sans-serif;
    letter-spacing: 1px;
    position: relative;
    overflow: hidden;
    
    i {
      font-size: 1.2rem;
    }
    
    .button-glitch {
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      transition: 0.6s;
    }
    
    &:hover {
      background: rgba(196, 30, 58, 0.8);
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(196, 30, 58, 0.5);
      
      .button-glitch {
        left: 100%;
      }
    }
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    
    p {
      font-size: 0.9rem;
    }
    
    .logout-button {
      padding: 0.8rem;
    }
  }
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: all 0.3s;
}

.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
  
  .settings-modal {
    transform: scale(0.95);
  }
}

// Add styles for ambient slider
.ambient-slider {
  &::-webkit-slider-thumb {
    background: #4CAF50; // Green color to differentiate from regular sound slider
  }
  
  &::-moz-range-thumb {
    background: #4CAF50;
  }
}

// Nova classe para ajustar scroll em dispositivos móveis
.modal-content-mobile {
  @media (max-width: 480px) {
    -webkit-overflow-scrolling: touch; // Melhora scroll em iOS
    &::-webkit-scrollbar {
      width: 4px; // Scrollbar mais fino em mobile
    }
  }
}

// Ajuste para modais em landscape
@media (max-height: 600px) and (orientation: landscape) {
  .settings-modal {
    max-height: 98vh;
    margin: 0.5rem;
  }
  
  .setting-row {
    margin-bottom: 0.8rem;
    padding: 0.6rem;
  }
  
  .music-player {
    margin-top: 1rem;
  }
}
</style>
